import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Trash2, Settings, Upload, ImagePlus, Video, Plus, ExternalLink, Lock, Eye, X, Globe,
} from 'lucide-react';
import { Skill, Role, ProjectType, Project } from '../../types';
import { localized, LANGUAGES, slugify } from '../../utils';
import { BACKEND_URL } from '../../config';
import { FormSection } from '../ui';
import FormShell from './FormShell';
import SkillPicker from './SkillPicker';
import { ProjectView } from '../../pages/Project';
import SkillManagerModal from '../SkillManagerModal';
import RoleManagerModal from '../RoleManagerModal';
import ProjectTypeManagerModal from '../ProjectTypeManagerModal';

// ─── Types ────────────────────────────────────────────────────────────────────

type ImageItem =
  | { kind: 'existing'; url: string }
  | { kind: 'new'; file: File; preview: string };

export interface ProjectFormData {
  title: string;
  titles: Record<string, string>;
  description: Record<string, string>;
  subtitle: Record<string, string>;
  typeId: number | null;
  notStarted: boolean;
  startDate: string;
  endDate: string;
  isOngoing: boolean;
  team: number;
  isPublic: boolean;
  featured: boolean;
  githubUrl: string;
  playStoreUrl: string;
  appStoreUrl: string;
  websiteUrl: string;
  version: string;
  lastUpdated: string;
  externalLinks: { title: Record<string, string>; url: string }[];
  privateLinks: { title: Record<string, string>; url: string }[];
  selectedSkills: Skill[];
  selectedRoles: Role[];
  logo: File | null;
  banner: File | null;
  showcaseBanner: File | null;
  existingLogoUrl?: string | null;
  existingBannerUrl?: string | null;
  existingShowcaseBannerUrl?: string | null;
  images: File[];
  videos: string[];
  existingImages: string[];
}

interface ProjectFormProps {
  mode: 'new' | 'edit';
  initialData?: Partial<ProjectFormData>;
  onSubmit: (data: ProjectFormData) => Promise<void>;
  submitting: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProjectForm({ mode, initialData, onSubmit, submitting }: ProjectFormProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // ── Core state ──────────────────────────────────────────────────────────────
  const [titles, setTitles] = useState<Record<string, string>>(initialData?.titles ?? { fr: '', en: '' });
  // base title (slug source, fallback): first filled language, else the stored one
  const title = LANGUAGES.map(l => titles[l.code]?.trim()).find(Boolean) ?? initialData?.title ?? '';
  const [description, setDescription] = useState<Record<string, string>>(
    initialData?.description ?? { fr: '', en: '' }
  );
  const [subtitle, setSubtitle] = useState<Record<string, string>>(initialData?.subtitle ?? { fr: '', en: '' });
  const [typeId, setTypeId] = useState<number | null>(initialData?.typeId ?? null);
  const [team, setTeam] = useState(initialData?.team ?? 1);
  const [isPublic, setIsPublic] = useState(initialData?.isPublic ?? true);
  const [featured, setFeatured] = useState(initialData?.featured ?? false);

  // ── Optional field values ───────────────────────────────────────────────────
  const [githubUrl, setGithubUrl] = useState(initialData?.githubUrl ?? '');
  const [version, setVersion] = useState(initialData?.version ?? '');
  const [lastUpdated, setLastUpdated] = useState(initialData?.lastUpdated ?? '');
  const [playStoreUrl, setPlayStoreUrl] = useState(initialData?.playStoreUrl ?? '');
  const [appStoreUrl, setAppStoreUrl] = useState(initialData?.appStoreUrl ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(initialData?.websiteUrl ?? '');
  const [externalLinks, setExternalLinks] = useState<{ title: Record<string, string>; url: string }[]>(
    initialData?.externalLinks ?? []
  );
  const [privateLinks, setPrivateLinks] = useState<{ title: Record<string, string>; url: string }[]>(
    initialData?.privateLinks ?? []
  );

  // ── Dates section ───────────────────────────────────────────────────────────
  const [notStarted, setNotStarted] = useState(initialData?.notStarted ?? false);
  const [startDate, setStartDate] = useState(initialData?.startDate ?? '');
  const [endDate, setEndDate] = useState(initialData?.endDate ?? '');
  const [isOngoing, setIsOngoing] = useState(initialData?.isOngoing ?? false);

  // ── Skills / Roles ──────────────────────────────────────────────────────────
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>(initialData?.selectedSkills ?? []);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(initialData?.selectedRoles ?? []);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [linkTab, setLinkTab] = useState<'public' | 'private'>('public');
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [allTypes, setAllTypes] = useState<ProjectType[]>([]);

  // ── Media ───────────────────────────────────────────────────────────────────
  const [logo, setLogo] = useState<File | null>(initialData?.logo ?? null);
  const [logoPreview, setLogoPreview] = useState<string | null>(initialData?.existingLogoUrl ?? null);
  const [banner, setBanner] = useState<File | null>(initialData?.banner ?? null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(initialData?.existingBannerUrl ?? null);
  const [showcaseBanner, setShowcaseBanner] = useState<File | null>(initialData?.showcaseBanner ?? null);
  const [showcasePreview, setShowcasePreview] = useState<string | null>(initialData?.existingShowcaseBannerUrl ?? null);
  const [imageItems, setImageItems] = useState<ImageItem[]>(() => [
    ...(initialData?.existingImages ?? []).map(url => ({ kind: 'existing' as const, url })),
    ...(initialData?.images ?? []).map(file => ({ kind: 'new' as const, file, preview: URL.createObjectURL(file) })),
  ]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [gapIdx, setGapIdx] = useState<number | null>(null);
  const [videos, setVideos] = useState<string[]>(initialData?.videos ?? []);
  const [videoInput, setVideoInput] = useState('');
  const [descLang, setDescLang] = useState(lang);
  const [previewOpen, setPreviewOpen] = useState(false);

  // ── Modals ──────────────────────────────────────────────────────────────────
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [skillModalOpen, setSkillModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);

  // ── CSS helpers ─────────────────────────────────────────────────────────────
  const inputCls = "glass rounded-lg w-full border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition text-sm";
  const selectCls = "glass rounded-lg border border-ink/[0.08] text-zinc-200 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition text-sm";
  const labelCls = "block text-xs font-medium text-zinc-400 mb-1.5";

  // ── Fetch skills / roles / types ────────────────────────────────────────────
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/skills`).then(r => r.json()).then(setAllSkills).catch(console.error);
    fetch(`${BACKEND_URL}/api/roles`).then(r => r.json()).then(setAllRoles).catch(console.error);
    fetch(`${BACKEND_URL}/api/project-types`).then(r => r.json()).then(setAllTypes).catch(console.error);
  }, []);

  useEffect(() => {
    setAvailableRoles(
      allRoles
        .filter(r => !selectedRoles.some(e => e.id === r.id))
        .sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang))
    );
  }, [allRoles, selectedRoles, lang]);

  // Escape closes the preview overlay
  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreviewOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewOpen]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const addRole = (id: number) => {
    const role = availableRoles.find(r => r.id === id);
    if (role) setSelectedRoles(prev => [...prev, role]);
  };

  const toggleSkill = (skill: Skill) =>
    setSelectedSkills(prev => (prev.some(e => e.id === skill.id) ? prev.filter(e => e.id !== skill.id) : [...prev, skill]));

  const publicCount = [githubUrl, websiteUrl, playStoreUrl, appStoreUrl].filter(Boolean).length + externalLinks.filter(l => l.url).length;
  const privateCount = privateLinks.filter(l => l.url).length;

  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f =>
      !imageItems.some(i => i.kind === 'new' && i.file.name === f.name)
    );
    setImageItems(prev => [...prev, ...files.map(file => ({ kind: 'new' as const, file, preview: URL.createObjectURL(file) }))]);
  };

  const handleImagesDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files)
      .filter(f => f.type.startsWith('image/') && !imageItems.some(i => i.kind === 'new' && i.file.name === f.name));
    setImageItems(prev => [...prev, ...files.map(file => ({ kind: 'new' as const, file, preview: URL.createObjectURL(file) }))]);
  };

  const addVideo = () => {
    if (videoInput.trim()) { setVideos(prev => [...prev, videoInput.trim()]); setVideoInput(''); }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      title, titles, description, subtitle, typeId, notStarted,
      startDate: notStarted ? '' : startDate,
      endDate: notStarted || isOngoing ? '' : endDate,
      isOngoing: isOngoing && !notStarted,
      team, isPublic, featured, githubUrl, playStoreUrl, appStoreUrl, websiteUrl,
      version, lastUpdated,
      externalLinks: externalLinks.filter(l => l.url.trim()),
      privateLinks: privateLinks.filter(l => l.url.trim()),
      selectedSkills, selectedRoles,
      logo, banner, showcaseBanner,
      existingImages: imageItems.filter(i => i.kind === 'existing').map(i => (i as { kind: 'existing'; url: string }).url),
      images: imageItems.filter(i => i.kind === 'new').map(i => (i as { kind: 'new'; file: File; preview: string }).file),
      videos,
    });
  };

  // ── Preview: the form state shaped like an API project ───────────────────────
  const toDisplayDate = (d: string) => (d ? d.split('-').reverse().join('/') : '');
  const langSwitch = (
    <div className="flex gap-2" role="group" aria-label={t('language') as string}>
      {LANGUAGES.map(({ code }) => (
        <button key={code} type="button" onClick={() => setDescLang(code)} aria-pressed={descLang === code}
          className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors ${descLang === code ? 'bg-primary text-white' : 'bg-surface text-muted hover:text-ink'}`}>
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  const preview: Project = {
    id: 0,
    strid: slugify(title), // legacy (non-URL) media only resolve when the title is unchanged
    title, titles, description, subtitle,
    type: allTypes.find(pt => pt.id === typeId)?.name ?? {},
    start_date: notStarted ? '' : toDisplayDate(startDate),
    end_date: notStarted || isOngoing ? null : toDisplayDate(endDate) || null,
    ongoing: isOngoing && !notStarted, not_started: notStarted, team,
    logo: logoPreview, banner: bannerPreview, showcase_banner: showcasePreview,
    github_url: githubUrl || null, version: version || null, last_updated: toDisplayDate(lastUpdated) || null,
    is_public: isPublic,
    external_links: externalLinks.filter(l => l.url.trim()),
    play_store_url: playStoreUrl, app_store_url: appStoreUrl, website_url: websiteUrl,
    roles: selectedRoles, skills: selectedSkills,
    images: imageItems.map(i => (i.kind === 'existing' ? i.url : i.preview)),
    videos,
  };

  const checkCls = 'inline-flex items-center gap-2 min-h-[44px] text-sm text-ink cursor-pointer';
  const iconBtn = 'p-2.5 rounded-lg border border-ink/[0.08] text-zinc-500 hover:text-primary-light hover:border-primary/30 hover:bg-primary/5 transition-all flex-shrink-0';
  const trashBtn = 'p-2.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-all';

  // Insertion point `at` (0..n) between images; dropping anywhere else does nothing
  const gapStrip = (at: number, trailing = false) => {
    const noop = dragIdx === at || dragIdx === at - 1;
    return (
      <div
        onDragOver={e => { if (noop) return; e.preventDefault(); setGapIdx(at); }}
        onDragLeave={() => setGapIdx(g => (g === at ? null : g))}
        onDrop={e => {
          e.preventDefault();
          if (dragIdx === null || noop) return;
          setImageItems(prev => {
            const next = [...prev];
            const [moved] = next.splice(dragIdx, 1);
            next.splice(at > dragIdx ? at - 1 : at, 0, moved);
            return next;
          });
          setDragIdx(null); setGapIdx(null);
        }}
        className={`absolute top-0 bottom-0 z-10 w-4 flex justify-center ${trailing ? '-right-3' : '-left-3'}`}
      >
        <span className={`h-full w-1 rounded-full bg-primary transition-opacity ${gapIdx === at ? 'opacity-100' : 'opacity-0'}`} />
      </div>
    );
  };

  const imageSlot = (label: string, src: string | null, onPick: (f: File) => void, onClear: () => void) => (
    <div>
      <p className={labelCls}>{label}</p>
      <div className="relative">
        <label className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-ink/[0.08] bg-surface text-zinc-500 hover:border-primary/40 hover:text-primary-light cursor-pointer transition-all overflow-hidden">
          {src ? <img src={src} alt="" className="w-full h-full object-contain" /> : <Upload size={18} />}
          <input type="file" accept="image/*" className="hidden" aria-label={label}
            onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
        </label>
        {src && (
          <button type="button" onClick={onClear} aria-label={t('delete') as string}
            className="absolute top-2 right-2 bg-red-600/90 hover:bg-red-500 text-white p-1.5 rounded-lg transition-colors">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );

  const linkRows = (
    links: { title: Record<string, string>; url: string }[],
    setLinks: (l: { title: Record<string, string>; url: string }[]) => void,
    icon: React.ReactNode,
  ) => links.map((link, idx) => (
    <div key={idx} className="flex gap-2">
      <input className={`${inputCls} !w-1/3`} placeholder={`${t('link_title')} (${lang.toUpperCase()})`} value={link.title[lang] || ''}
        onChange={e => setLinks(links.map((l, i) => (i === idx ? { ...l, title: { ...l.title, [lang]: e.target.value } } : l)))} />
      <div className="relative flex-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">{icon}</span>
        <input className={`${inputCls} !pl-9`} placeholder={t('link_url') as string} value={link.url}
          onChange={e => setLinks(links.map((l, i) => (i === idx ? { ...l, url: e.target.value } : l)))} />
      </div>
      <button type="button" onClick={() => setLinks(links.filter((_, i) => i !== idx))} className={trashBtn} aria-label={t('delete') as string}>
        <Trash2 size={15} />
      </button>
    </div>
  ));

  const sections: [string, string][] = [
    ['pf-identity', String(t('identity'))],
    ['pf-description', String(t('description'))],
    ['pf-media', String(t('media'))],
    ['pf-skills', String(t('skills_skills'))],
    ['pf-roles', String(t('roles'))],
    ['pf-links', String(t('links'))],
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <FormShell
        onSubmit={handleFormSubmit}
        submitting={submitting}
        submitLabel={String(mode === 'new' ? t('create_project') : t('save'))}
        sections={sections}
        side={
          <div className="rounded-2xl border border-line p-5">
            <p className="font-display font-extrabold tracking-[-0.02em] text-lg text-ink">{t('publication')}</p>
            <div className="mt-1 flex flex-wrap gap-x-6">
              <label className={checkCls}>
                <input type="checkbox" className="w-4 h-4 accent-primary" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
                {t('public')}
              </label>
              <label className={checkCls}>
                <input type="checkbox" className="w-4 h-4 accent-primary" checked={featured} onChange={e => setFeatured(e.target.checked)} />
                {t('showcase')}
              </label>
            </div>
            <button type="button" onClick={() => setPreviewOpen(true)}
              className="mt-2 w-full h-11 rounded-full border border-line text-sm font-semibold text-ink hover:bg-surface transition-colors inline-flex items-center justify-center gap-2">
              <Eye size={16} /> {t('see_preview')}
            </button>
          </div>
        }
      >
        <FormSection id="pf-identity" title={String(t('identity'))}>
          {langSwitch}
          <label className="block">
            <span className={labelCls}>{t('title')} ({descLang.toUpperCase()}) *</span>
            <input className={inputCls} value={titles[descLang] || ''} placeholder={title} onChange={e => setTitles(prev => ({ ...prev, [descLang]: e.target.value }))} required={!title} />
          </label>
          <label className="block">
            <span className={labelCls}>{t('subtitle')} ({descLang.toUpperCase()})</span>
            <input className={inputCls} value={subtitle[descLang] || ''} onChange={e => setSubtitle(prev => ({ ...prev, [descLang]: e.target.value }))} />
            <span className="block text-xs text-zinc-500 mt-1.5">{t('subtitle_hint')}</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-[3fr_1fr] gap-4">
            <div>
              <label htmlFor="pf-type" className={labelCls}>{t('type')} *</label>
              <div className="flex gap-2">
                <select id="pf-type" className={`flex-1 ${selectCls}`} value={typeId ?? ''} onChange={e => setTypeId(e.target.value ? +e.target.value : null)} required>
                  <option value="" disabled>{t('select_type')}</option>
                  {[...allTypes]
                    .sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang))
                    .map(pt => <option key={pt.id} value={pt.id}>{localized(pt.name, lang)}</option>)}
                </select>
                <button type="button" onClick={() => setTypeModalOpen(true)} className={iconBtn} aria-label="Manage types"><Settings size={16} /></button>
              </div>
            </div>
            <label className="block">
              <span className={labelCls}>{t('team_size')}</span>
              <input type="number" className={inputCls} value={team} onChange={e => setTeam(Math.max(1, +e.target.value))} min={1} required />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className={labelCls}>{t('start_date')}</span>
              <input type="date" className={`w-full ${selectCls} disabled:opacity-40`} value={startDate} disabled={notStarted} onChange={e => setStartDate(e.target.value)} />
            </label>
            <label className="block">
              <span className={labelCls}>{t('end_date')}</span>
              <input type="date" className={`w-full ${selectCls} disabled:opacity-40`} value={endDate} disabled={notStarted || isOngoing} onChange={e => setEndDate(e.target.value)} />
            </label>
          </div>
          <div className="flex flex-wrap gap-x-6">
            <label className={checkCls}>
              <input type="checkbox" className="w-4 h-4 accent-primary" checked={isOngoing} disabled={notStarted}
                onChange={e => setIsOngoing(e.target.checked)} />
              {t('on_going')}
            </label>
            <label className={checkCls}>
              <input type="checkbox" className="w-4 h-4 accent-primary" checked={notStarted}
                onChange={e => setNotStarted(e.target.checked)} />
              {t('not_started')}
            </label>
          </div>
        </FormSection>

        <FormSection id="pf-description" title={String(t('description'))}>
          {langSwitch}
          {LANGUAGES.map(({ code }) => (
            <div key={code} className={code !== descLang ? 'hidden' : ''}>
              <ReactQuill value={description[code] || ''} onChange={v => setDescription(prev => ({ ...prev, [code]: v }))} placeholder={t('description') as string} />
            </div>
          ))}
        </FormSection>

        <FormSection id="pf-media" title={String(t('media'))}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {imageSlot('Logo (512×512)', logoPreview, f => { setLogo(f); setLogoPreview(URL.createObjectURL(f)); }, () => { setLogo(null); setLogoPreview(null); })}
            {imageSlot('Banner (1680×720)', bannerPreview, f => { setBanner(f); setBannerPreview(URL.createObjectURL(f)); }, () => { setBanner(null); setBannerPreview(null); })}
            {imageSlot(`${t('showcase_banner')} (1500×1200)`, showcasePreview, f => { setShowcaseBanner(f); setShowcasePreview(URL.createObjectURL(f)); }, () => { setShowcaseBanner(null); setShowcasePreview(null); })}
          </div>

          <div>
            <p className={labelCls}>Images</p>
            <label
              className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-ink/[0.08] rounded-lg p-4 text-center text-zinc-500 hover:border-primary/40 hover:text-primary-light hover:bg-primary/5 cursor-pointer transition-all mb-3"
              onDragOver={e => e.preventDefault()}
              onDrop={handleImagesDrop}
            >
              <ImagePlus size={16} />
              <span className="text-sm">{t('add_images')}</span>
              <input type="file" multiple accept="image/*" className="hidden" onChange={handleImagesChange} />
            </label>
            {imageItems.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {imageItems.map((item, idx) => (
                  <div key={idx} className="relative">
                    {/* Drop gap before this tile: only shown while dragging, hovering it shows where the image will land */}
                    {dragIdx !== null && gapStrip(idx)}
                    {dragIdx !== null && idx === imageItems.length - 1 && gapStrip(idx + 1, true)}
                    <div
                      draggable
                      onDragStart={() => setDragIdx(idx)}
                      onDragEnd={() => { setDragIdx(null); setGapIdx(null); }}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 group cursor-grab active:cursor-grabbing transition-all ${
                        item.kind === 'new' ? 'border-primary/60' : 'border-ink/[0.08]'
                      } ${dragIdx === idx ? 'opacity-40 scale-95' : ''}`}
                    >
                      <img src={item.kind === 'existing' ? item.url : item.preview} alt="" className="w-full h-full object-cover" />
                      {item.kind === 'new' && (
                        <span className="absolute top-1 left-1 text-[9px] font-bold bg-primary/80 text-white px-1.5 py-0.5 rounded">NEW</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setImageItems(prev => prev.filter((_, i) => i !== idx))}
                        aria-label={t('delete') as string}
                        className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className={labelCls}>Vidéos</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Video size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                  className={`${inputCls} !pl-9`}
                  placeholder={t('video_url') as string}
                  value={videoInput}
                  onChange={e => setVideoInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } }}
                />
              </div>
              <button type="button" onClick={addVideo} className="px-3.5 py-2.5 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors" aria-label="Add video">
                <Plus size={18} />
              </button>
            </div>
            {videos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {videos.map((v, i) => (
                  <span key={i} className="flex items-center gap-1.5 bg-surface border border-line px-3 py-1.5 rounded-lg text-sm text-zinc-300 max-w-full">
                    <span className="truncate">{v}</span>
                    <button type="button" onClick={() => setVideos(prev => prev.filter(x => x !== v))} className="text-red-400/60 hover:text-red-400 transition-colors flex-shrink-0" aria-label={t('delete') as string}>
                      <Trash2 size={13} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </FormSection>

        <FormSection id="pf-skills" title={String(t('skills_skills'))} count={selectedSkills.length}>
          <SkillPicker skills={allSkills} selected={selectedSkills} onToggle={toggleSkill} onManage={() => setSkillModalOpen(true)} />
        </FormSection>

        <FormSection id="pf-roles" title={String(t('roles'))} count={selectedRoles.length}>
          <div className="flex gap-2">
            <select className={`flex-1 ${selectCls}`} value="" onChange={e => addRole(+e.target.value)} aria-label={t('select_role') as string}>
              <option value="" disabled>{t('select_role')}</option>
              {availableRoles.map(r => <option key={r.id} value={r.id}>{localized(r.name, lang)}</option>)}
            </select>
            <button type="button" onClick={() => setRoleModalOpen(true)} className={iconBtn} aria-label="Manage roles"><Settings size={16} /></button>
          </div>
          {selectedRoles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedRoles.map(role => (
                <span key={role.id} className="flex items-center gap-1.5 bg-surface border border-line px-3 py-1.5 rounded-lg text-sm text-zinc-300">
                  {localized(role.name, lang)}
                  <button type="button" onClick={() => setSelectedRoles(prev => prev.filter(r => r.id !== role.id))} className="text-red-400/60 hover:text-red-400 transition-colors" aria-label={t('delete') as string}>
                    <Trash2 size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </FormSection>

        <FormSection id="pf-links" title={String(t('links'))}>
          <div role="group" aria-label={t('links') as string} className="flex flex-wrap gap-3">
            {([
              ['public', t('public_links'), t('public_links_description'), publicCount, <Globe size={20} />, 'border-primary bg-surface', 'bg-primary/10 text-primary'],
              ['private', t('private_links_tab'), t('private_links_description'), privateCount, <Lock size={20} />, 'border-amber-600 bg-amber-500/10', 'bg-amber-500/15 text-amber-600'],
            ] as const).map(([id, title, hint, n, icon, onCls, iconCls]) => (
              <button key={id} type="button" aria-pressed={linkTab === id} onClick={() => setLinkTab(id)}
                className={`flex flex-1 basis-64 items-center gap-3.5 min-h-[72px] px-4 py-3 rounded-2xl border-2 text-left transition-colors ${linkTab === id ? onCls : 'border-line hover:bg-surface'}`}>
                <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconCls}`}>{icon}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold text-ink">{title}</span>
                  <span className="block text-[13px] text-zinc-500">{hint}</span>
                </span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${iconCls}`}>{n}</span>
              </button>
            ))}
          </div>

          {linkTab === 'public' ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block"><span className={labelCls}>{t('github_url')}</span>
                  <input className={inputCls} placeholder="https://github.com/..." value={githubUrl} onChange={e => setGithubUrl(e.target.value)} /></label>
                <label className="block"><span className={labelCls}>{t('website')}</span>
                  <input className={inputCls} placeholder="https://..." value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} /></label>
                <label className="block"><span className={labelCls}>Google Play</span>
                  <input className={inputCls} placeholder="https://play.google.com/..." value={playStoreUrl} onChange={e => setPlayStoreUrl(e.target.value)} /></label>
                <label className="block"><span className={labelCls}>App Store (iOS)</span>
                  <input className={inputCls} placeholder="https://apps.apple.com/..." value={appStoreUrl} onChange={e => setAppStoreUrl(e.target.value)} /></label>
              </div>
              <div className="space-y-2">
                <p className={labelCls}>{t('external_links')}</p>
                {linkRows(externalLinks, setExternalLinks, <ExternalLink size={14} />)}
                <button type="button" onClick={() => setExternalLinks(prev => [...prev, { title: { fr: '', en: '' }, url: '' }])}
                  className="inline-flex items-center gap-1.5 min-h-[36px] text-xs text-primary-light font-medium">
                  <Plus size={14} /> {t('add_external_link')}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              {linkRows(privateLinks, setPrivateLinks, <Lock size={14} className="text-amber-500/50" />)}
              <button type="button" onClick={() => setPrivateLinks(prev => [...prev, { title: { fr: '', en: '' }, url: '' }])}
                className="inline-flex items-center gap-1.5 min-h-[36px] text-xs text-amber-500 font-medium">
                <Plus size={14} /> {t('add_private_link')}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-line">
            <label className="block"><span className={labelCls}>{t('version')}</span>
              <input className={inputCls} placeholder="1.0.0" value={version} onChange={e => setVersion(e.target.value)} /></label>
            <label className="block"><span className={labelCls}>{t('last_updated')}</span>
              <input type="date" className={`w-full ${selectCls}`} value={lastUpdated} onChange={e => setLastUpdated(e.target.value)} /></label>
          </div>
        </FormSection>
      </FormShell>

      {/* Portal: the page wrapper's z-index would keep the site header above the overlay */}
      {previewOpen && createPortal(
        <div role="dialog" aria-modal="true" aria-label={t('preview') as string} className="fixed inset-0 z-[60] bg-base overflow-y-auto">
          <div className="sticky top-0 z-10 h-16 bg-base/95 border-b border-line flex items-center justify-between px-4 sm:px-8">
            <span className="text-sm font-semibold text-muted">{t('preview')}</span>
            <button type="button" onClick={() => setPreviewOpen(false)} autoFocus
              className="h-11 px-4 inline-flex items-center gap-2 rounded-full bg-ink text-base text-sm font-semibold hover:bg-primary hover:text-white transition-colors">
              <X size={16} /> {t('close')}
            </button>
          </div>
          <div className="-mt-16">
            <ProjectView project={preview} authenticated={false} onBack={() => setPreviewOpen(false)} />
          </div>
        </div>,
        document.body,
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      <SkillManagerModal
        open={skillModalOpen}
        onClose={() => setSkillModalOpen(false)}
        onCreated={skill => setAllSkills(prev => [...prev, skill])}
        onDeleted={id => { setAllSkills(prev => prev.filter(s => s.id !== id)); setSelectedSkills(prev => prev.filter(s => s.id !== id)); }}
      />
      <RoleManagerModal
        open={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        onCreated={role => setAllRoles(prev => [...prev, role])}
        onDeleted={id => { setAllRoles(prev => prev.filter(r => r.id !== id)); setSelectedRoles(prev => prev.filter(r => r.id !== id)); }}
      />
      <ProjectTypeManagerModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onCreated={type => setAllTypes(prev => [...prev, type])}
        onDeleted={id => setAllTypes(prev => prev.filter(t => t.id !== id))}
      />
    </>
  );
}
